/**
 * RoundOverview — the "Resumen IA" panel shown ABOVE the expediente grid on the
 * round dashboard (`/rounds/:roundId`). Renders the cached, Gemini-generated
 * Spanish recap from `GET /v1/rounds/{round_id}/overview` (RoundOverviewResponse).
 *
 * The summary is generated ONCE server-side and cached on the round row; this
 * component just reads it, with a "Regenerar" affordance that re-requests with
 * `regenerate=true`. It is cosmetic: until the round is completed the endpoint
 * returns `available: false` and the panel hides itself; a generation error shows
 * a compact, non-blocking notice rather than breaking the dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { api, IS_DEMO } from "../api/client";
import type { RoundOverviewResponse } from "../api/client";

/** Synthetic overview for DEMO mode (no API base URL configured). */
const DEMO_OVERVIEW: RoundOverviewResponse = {
  available: true,
  summary:
    "De los 1,240 expedientes procesados, el 90 % fue aprobado, cerca del 6 % " +
    "rechazado y alrededor del 3 % quedó en revisión humana. El principal motivo " +
    "de rechazo fue la CURP que no coincide, seguida de la falta de coincidencia " +
    "facial; entre los detonantes de revisión humana destacan la confianza facial " +
    "media y los nombres que no coinciden.",
  model: "gemini-flash-lite-latest",
  generated_at: "2026-06-30T12:00:00Z",
  cached: true,
};

/** Localized "generado el …" timestamp, or null for an unparseable value. */
function formatGeneratedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

/** The subtle "Resumen IA" label + sparkle icon shared across panel states. */
function OverviewLabel() {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: "center", color: "primary.main" }}
    >
      <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
      <Typography variant="overline" sx={{ color: "primary.main" }}>
        Resumen IA
      </Typography>
    </Stack>
  );
}

export function RoundOverview({ roundId }: { roundId: string }) {
  const theme = useTheme();
  const [data, setData] = useState<RoundOverviewResponse | null>(
    IS_DEMO ? DEMO_OVERVIEW : null,
  );
  const [loading, setLoading] = useState(!IS_DEMO);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const load = useCallback(
    async (regenerate: boolean) => {
      if (IS_DEMO) return;
      if (regenerate) setRegenerating(true);
      else setLoading(true);
      setError(false);
      try {
        const res = await api.GET("/rounds/{round_id}/overview", {
          params: {
            path: { round_id: roundId },
            query: regenerate ? { regenerate: true } : {},
          },
        });
        setData(res.data ?? null);
      } catch {
        // Cosmetic panel — never surface a blocking error; show a compact notice.
        setError(true);
      } finally {
        setLoading(false);
        setRegenerating(false);
      }
    },
    [roundId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Initial generation can take a moment (a Gemini call on first request). Keep
  // the spinner compact — this sits above the grid, not the whole page.
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <OverviewLabel />
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", mt: 1 }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Generando el resumen de la ronda…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // Generation failed on initial load — compact, non-blocking, with a retry.
  if (error && !data?.summary) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => load(false)}>
            Reintentar
          </Button>
        }
      >
        No se pudo generar el resumen IA de esta ronda.
      </Alert>
    );
  }

  // Round not completed yet (available: false) or nothing to show — hide entirely.
  if (!data || !data.available || !data.summary) return null;

  const generatedAt = formatGeneratedAt(data.generated_at);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
      >
        <OverviewLabel />
        <Button
          size="small"
          variant="text"
          startIcon={
            regenerating ? <CircularProgress size={14} /> : <RefreshIcon />
          }
          disabled={regenerating}
          onClick={() => load(true)}
        >
          Regenerar
        </Button>
      </Stack>

      <Typography variant="body1" sx={{ color: "text.primary" }}>
        {data.summary}
      </Typography>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Generado por IA{generatedAt ? ` · ${generatedAt}` : ""}. Puede contener
          imprecisiones.
        </Typography>
      </Box>
    </Paper>
  );
}
